import { useRef, useState } from "react";
import { MapPin, Check, Trash2, Clock } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const fmtTs = (t) => {
  const s = Math.max(0, Math.floor(Number(t) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const VideoWithPins = ({ file, notes, toAbsolute, canPin, onPin, index }) => {
  const videoRef = useRef(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [text, setText] = useState("");
  const isVideo = file.content_type?.startsWith("video/");

  const seekTo = (t) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
    videoRef.current.pause();
  };

  const pin = async () => {
    if (!text.trim()) return;
    videoRef.current?.pause();
    await onPin({ file_url: file.url, timestamp: isVideo ? (videoRef.current?.currentTime || 0) : 0, text: text.trim() });
    setText("");
  };

  return (
    <div className="space-y-3" data-testid={`review-media-${index}`}>
      <div className="rounded-xl overflow-hidden bg-black border border-white/10">
        {isVideo ? (
          <video
            ref={videoRef}
            src={toAbsolute(file.url)}
            controls
            playsInline
            preload="metadata"
            className="w-full max-h-[440px] bg-black"
            onTimeUpdate={(e) => setTime(e.currentTarget.currentTime || 0)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            data-testid={`review-video-${index}`}
          />
        ) : (
          <img src={toAbsolute(file.url)} alt={file.filename} className="w-full max-h-[440px] object-contain bg-black" />
        )}
        {isVideo && duration > 0 && (
          <div className="relative h-2 mx-3 my-2 rounded-full bg-white/10" data-testid={`review-marker-bar-${index}`}>
            {notes.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => seekTo(n.timestamp)}
                title={`${fmtTs(n.timestamp)} — ${n.text}`}
                className={`absolute -top-1 h-4 w-4 -ml-2 rounded-full border-2 border-black ${n.resolved ? "bg-emerald-400" : "bg-nexus-pink"} hover:scale-125 transition-transform`}
                style={{ left: `${Math.min(100, (n.timestamp / duration) * 100)}%` }}
                data-testid={`review-marker-${n.id}`}
              />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-zinc-400">
          <span className="truncate">{file.filename}</span>
          <a href={toAbsolute(file.url)} download={file.filename} target="_blank" rel="noreferrer" className="text-nexus-blue hover:underline shrink-0" data-testid={`download-deliverable-${index}`}>Download</a>
        </div>
      </div>

      {canPin && (
        <div className="flex gap-2 items-center" data-testid={`pin-note-form-${index}`}>
          <span className="shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-nexus-pink/15 text-nexus-pink tabular-nums" data-testid={`pin-note-time-${index}`}>
            {isVideo ? `@ ${fmtTs(time)}` : "Note"}
          </span>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && pin()}
            placeholder={isVideo ? "Pause the video and describe the change here…" : "What should change?"}
            className="bg-white/[0.02] border-white/10 text-white h-9"
            data-testid={`pin-note-input-${index}`}
          />
          <Button onClick={pin} disabled={!text.trim()} className="nexus-gradient text-white rounded-full border-0 h-9 px-4 shrink-0" data-testid={`pin-note-btn-${index}`}>
            <MapPin className="h-3.5 w-3.5 mr-1.5" /> Pin
          </Button>
        </div>
      )}

      {notes.length > 0 && (
        <ol className="space-y-2" data-testid={`review-notes-${index}`}>
          {notes.map((n) => (
            <li key={n.id} className={`p-3 rounded-lg bg-white/[0.02] border border-white/5 ${n.resolved ? "opacity-60" : ""}`} data-testid={`review-note-${n.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <button
                  type="button"
                  onClick={() => seekTo(n.timestamp)}
                  className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${n.author_role === "brand" ? "bg-nexus-blue/15 text-nexus-blue" : "bg-nexus-pink/15 text-nexus-pink"}`}
                  data-testid={`review-note-seek-${n.id}`}
                >
                  @ {fmtTs(n.timestamp)}
                </button>
                <span className="text-[10px] uppercase tracking-widest text-zinc-600 flex items-center gap-1">
                  <Clock className="h-2.5 w-2.5" /> {n.author_name} · {n.author_role}
                </span>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{n.text}</p>
              <div className="flex items-center gap-3 mt-2">
                {n.resolved ? (
                  <span className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1"><Check className="h-3 w-3" /> Resolved</span>
                ) : n.onResolve ? (
                  <button type="button" onClick={() => n.onResolve(n)} className="text-[10px] uppercase tracking-widest text-emerald-300 hover:text-emerald-100 flex items-center gap-1" data-testid={`review-note-resolve-${n.id}`}>
                    <Check className="h-3 w-3" /> Mark done
                  </button>
                ) : null}
                {n.onDelete && (
                  <button type="button" onClick={() => n.onDelete(n)} className="text-[10px] uppercase tracking-widest text-red-300 hover:text-red-100 flex items-center gap-1 ml-auto" data-testid={`review-note-delete-${n.id}`}>
                    <Trash2 className="h-3 w-3" /> Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export const DeliverableReview = ({ files, notes, toAbsolute, role, canPin, onPin, onResolve, onDelete, userId }) => {
  if (!files?.length) return null;
  return (
    <div className="space-y-6" data-testid="deliverable-review">
      {files.map((f, i) => {
        const mine = notes
          .filter((n) => !n.file_url || n.file_url === f.url)
          .map((n) => ({
            ...n,
            onResolve: role === "creator" && onResolve ? onResolve : null,
            onDelete: n.author_id === userId && n.revision_round == null && onDelete ? onDelete : null,
          }));
        return <VideoWithPins key={f.url} file={f} notes={mine} toAbsolute={toAbsolute} canPin={canPin} onPin={onPin} index={i} />;
      })}
    </div>
  );
};
